/**
 * Thin wrapper around the browser SpeechRecognition API (the default, zero-dependency transcription
 * provider for the rules assistant). It transcribes live mic audio for up to `maxMs` and returns the
 * final transcript. The provider is intentionally isolated so a local/offline model (e.g. Whisper via
 * transformers.js) can be added later without touching the UI.
 *
 * NOTE: In Chromium browsers SpeechRecognition streams audio to the browser vendor's STT service, so
 * it is not fully offline/private — that trade-off is documented for the planned local provider.
 */

interface SpeechAlternative {
  transcript: string;
}
interface SpeechResult {
  isFinal: boolean;
  0: SpeechAlternative;
}
interface SpeechResultList {
  length: number;
  [index: number]: SpeechResult;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechResultList;
}
interface SpeechRecognitionErrorEventLike {
  error?: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechWindow {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

function getCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as SpeechWindow;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isSpeechSupported(): boolean {
  return getCtor() !== null;
}

export interface SpeechHandlers {
  /** Running transcript (final + interim), emitted as the GM speaks. */
  onPartial?: (text: string) => void;
  /** Final transcript once listening stops (timeout, silence or `stop()`). */
  onFinal: (text: string) => void;
  onError: (error: string) => void;
}

export interface SpeechOptions {
  lang?: string;
  maxMs?: number;
}

export interface SpeechSession {
  /** Stop listening and emit the final transcript. */
  stop: () => void;
  /** Cancel listening without emitting a final transcript. */
  abort: () => void;
}

export function startListening(
  handlers: SpeechHandlers,
  options: SpeechOptions = {},
): SpeechSession | null {
  const Ctor = getCtor();
  if (!Ctor) return null;

  const recognition = new Ctor();
  recognition.lang = options.lang ?? 'it-IT';
  recognition.continuous = true;
  recognition.interimResults = true;

  let finalText = '';
  let settled = false;
  const timer = window.setTimeout(() => {
    try {
      recognition.stop();
    } catch {
      /* already stopped */
    }
  }, options.maxMs ?? 45_000);

  recognition.onresult = (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i];
      if (result.isFinal) finalText += result[0].transcript;
      else interim += result[0].transcript;
    }
    handlers.onPartial?.(`${finalText} ${interim}`.trim());
  };

  recognition.onerror = (event) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    handlers.onError(event.error ?? 'speech-error');
  };

  recognition.onend = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    handlers.onFinal(finalText.trim());
  };

  recognition.start();

  return {
    stop() {
      try {
        recognition.stop();
      } catch {
        /* already stopped */
      }
    },
    abort() {
      if (!settled) {
        settled = true;
        window.clearTimeout(timer);
      }
      try {
        recognition.abort();
      } catch {
        /* already stopped */
      }
    },
  };
}
